import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Avatar,
  Badge,
  ButtonBase,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import SendIcon from '@mui/icons-material/Send';
import CloseIcon from '@mui/icons-material/Close';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import * as signalR from '@microsoft/signalr';
import { refreshChatAlerts } from '../../../../services/ecommerceChatAlerts';
import { ecommerceApi } from '../../../../config/apiClient';
import { ECOMMERCE_API_URL } from '../../../../config/api';
import {
  AgentTemplate,
  ChatMessage,
  ChatTopic,
  clockTime,
  relativeTime,
  SessionDetail,
  SessionRow,
  STATUS_META,
  topicLabel,
} from './liveChatTypes';

type Filter = 'open' | 'WaitingAgent' | 'Active' | 'Bot' | 'Closed' | 'all';

const FILTERS: { value: Filter; label: string; color?: string }[] = [
  { value: 'open', label: 'Açık' },
  { value: 'WaitingAgent', label: 'Bekleyen', color: '#f59e0b' },
  { value: 'Bot', label: 'Bot', color: '#3b82f6' },
  { value: 'Closed', label: 'Geçmiş', color: '#94a3b8' },
  { value: 'all', label: 'Tümü' },
];

function waitText(startedAt?: number | null) {
  if (!startedAt) return '';
  const min = Math.floor((Date.now() - startedAt) / 60000);
  if (min < 1) return 'az önce';
  if (min < 60) return `${min} dk`;
  return `${Math.floor(min / 60)} sa ${min % 60} dk`;
}

const STATUS_DOT: Record<string, string> = {
  WaitingAgent: '#f59e0b',
  Active: '#10b981',
  Bot: '#3b82f6',
  Closed: '#94a3b8',
};

interface Props {
  companyId?: string | null;
  agentName: string;
  templates: AgentTemplate[];
  topics?: ChatTopic[];
  onWaitingCount?: (count: number) => void;
  /** Bu id verilirse görüşme otomatik açılır (zil/toast). */
  focusSessionId?: string | null;
  focusNonce?: number;
  onSchemaError?: (message: string) => void;
}

function matchesFilter(session: SessionRow, filter: Filter) {
  if (filter === 'all') return true;
  if (filter === 'open') return session.status === 'WaitingAgent' || session.status === 'Active';
  return session.status === filter;
}

/** Ziyaretçinin bota sorduğu sorular: temsilci gelmeden önceki adımlar. */
function visitorPath(messages: ChatMessage[]) {
  const steps: string[] = [];
  for (const message of messages) {
    if (message.senderType === 'Agent') break;
    if (message.senderType === 'Visitor') steps.push(message.body);
  }
  return steps;
}

const LiveChatInbox: React.FC<Props> = ({ companyId, agentName, templates, topics, onWaitingCount, onSchemaError, focusSessionId, focusNonce }) => {
  const [sessions, setSessions] = useState<SessionRow[]>([]);
  const [waitLimit, setWaitLimit] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState<Filter>('open');
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selected, setSelected] = useState<SessionDetail | null>(null);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [slashIndex, setSlashIndex] = useState(0);
  const [, setTick] = useState(0);
  const messagesRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const selectedIdRef = useRef<string | null>(null);
  selectedIdRef.current = selectedId;

  const loadSessions = useCallback(async () => {
    setLoading(true);
    try {
      const res = await ecommerceApi.get('/api/ecommerce/chat/inbox?status=all');
      const loadedAt = Date.now();
      setSessions(((res.data?.data || []) as SessionRow[]).map((s) => ({
        ...s,
        waitStartedAt: typeof s.waitSeconds === 'number' ? loadedAt - s.waitSeconds * 1000 : null,
      })));
      setWaitLimit(Number(res.data?.agentWaitMinutes) || 0);
    } catch (error: any) {
      if (error?.response?.data?.code === 'SCHEMA') onSchemaError?.(error.response.data.message);
    } finally {
      setLoading(false);
    }
  }, [onSchemaError]);

  const applyDetail = useCallback((data?: SessionDetail) => {
    if (!data) return;
    setSelected(data);
    setSessions((prev) => prev.map((s) => (s.id === data.id ? { ...s, status: data.status, claimedByName: data.claimedByName, unread: 0 } : s)));
  }, []);

  const openSession = useCallback(async (id: string) => {
    setSelectedId(id);
    const res = await ecommerceApi.get(`/api/ecommerce/chat/inbox/${id}`);
    applyDetail(res.data?.data);
    // Okundu bilgisi zildeki sayıya hemen yansısın.
    refreshChatAlerts();
  }, [applyDetail]);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  useEffect(() => {
    if (!focusSessionId) return;
    setFilter('open');
    openSession(focusSessionId).catch(() => undefined);
  }, [focusSessionId, focusNonce, openSession]);

  // Bekleme sürelerinin canlı görünmesi için dakikada bir yeniden çiz.
  useEffect(() => {
    const timer = window.setInterval(() => setTick((t) => t + 1), 60000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!companyId) return;
    const conn = new signalR.HubConnectionBuilder()
      .withUrl(`${String(ECOMMERCE_API_URL).replace(/\/$/, '')}/hubs/ecomchat`)
      .withAutomaticReconnect()
      .build();
    const refresh = () => loadSessions();
    conn.on('ChatRequestCreated', refresh);
    conn.on('SessionClaimed', refresh);
    conn.on('NewChatMessage', (payload: { sessionId?: string }) => {
      refresh();
      if (payload?.sessionId && payload.sessionId === selectedIdRef.current) {
        openSession(payload.sessionId).catch(() => undefined);
      }
    });
    conn.onreconnected(() => conn.invoke('JoinCompany', companyId).catch(() => undefined));
    conn.start().then(() => conn.invoke('JoinCompany', companyId)).catch(() => undefined);
    return () => {
      conn.stop().catch(() => undefined);
    };
  }, [companyId, loadSessions, openSession]);

  const counts = useMemo(() => {
    const result: Record<Filter, number> = { open: 0, WaitingAgent: 0, Active: 0, Bot: 0, Closed: 0, all: sessions.length };
    sessions.forEach((s) => {
      FILTERS.forEach(({ value }) => {
        if (value !== 'all' && matchesFilter(s, value)) result[value] += 1;
      });
    });
    return result;
  }, [sessions]);

  useEffect(() => {
    onWaitingCount?.(counts.WaitingAgent);
  }, [counts.WaitingAgent, onWaitingCount]);

  const visible = useMemo(() => {
    const term = search.trim().toLocaleLowerCase('tr-TR');
    return sessions
      .filter((s) => matchesFilter(s, filter))
      .filter((s) => {
        if (!term) return true;
        return [s.visitorName, s.visitorEmail, topicLabel(topics, s.topic)]
          .filter(Boolean)
          .some((v) => String(v).toLocaleLowerCase('tr-TR').includes(term));
      })
      // Bekleyenler en üstte, en uzun bekleyen önce.
      .sort((a, b) => {
        const wa = a.status === 'WaitingAgent' ? 0 : 1;
        const wb = b.status === 'WaitingAgent' ? 0 : 1;
        if (wa !== wb) return wa - wb;
        const ta = new Date(a.lastMessageAt || 0).getTime();
        const tb = new Date(b.lastMessageAt || 0).getTime();
        return wa === 0 ? ta - tb : tb - ta;
      });
  }, [sessions, filter, search, topics]);

  const messages = useMemo(() => selected?.messages || [], [selected]);
  const path = useMemo(() => visitorPath(messages), [messages]);
  const closed = selected?.status === 'Closed';

  useEffect(() => {
    messagesRef.current?.scrollTo({ top: messagesRef.current.scrollHeight });
  }, [messages]);

  // "/" ile şablon arama
  const slashTerm = draft.startsWith('/') ? draft.slice(1).toLocaleLowerCase('tr-TR') : null;
  const slashMatches = useMemo(() => {
    if (slashTerm === null) return [];
    return templates
      .filter((t) => `${t.title} ${t.body}`.toLocaleLowerCase('tr-TR').includes(slashTerm))
      .slice(0, 6);
  }, [slashTerm, templates]);

  useEffect(() => setSlashIndex(0), [slashTerm]);

  const insertTemplate = (template: AgentTemplate) => {
    setDraft((prev) => {
      const base = prev.startsWith('/') ? '' : prev.trim();
      return base ? `${base}\n${template.body}` : template.body;
    });
    inputRef.current?.focus();
  };

  const post = async (url: string, body?: object) => {
    const res = await ecommerceApi.post(url, body);
    applyDetail(res.data?.data);
    loadSessions();
  };

  const send = async () => {
    if (!selectedId || !draft.trim() || closed || sending) return;
    setSending(true);
    try {
      await post(`/api/ecommerce/chat/inbox/${selectedId}/messages`, { agentName, text: draft.trim() });
      setDraft('');
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const claim = () => selectedId && post(`/api/ecommerce/chat/inbox/${selectedId}/claim`, { agentName });
  const close = () => selectedId && post(`/api/ecommerce/chat/inbox/${selectedId}/close`);

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (slashMatches.length > 0) {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setSlashIndex((i) => (i + 1) % slashMatches.length);
        return;
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        setSlashIndex((i) => (i - 1 + slashMatches.length) % slashMatches.length);
        return;
      }
      if (event.key === 'Enter' || event.key === 'Tab') {
        event.preventDefault();
        insertTemplate(slashMatches[slashIndex]);
        return;
      }
      if (event.key === 'Escape') {
        setDraft('');
        return;
      }
    }
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      send();
    }
  };

  const name = (s?: SessionRow | null) => s?.visitorName || 'Ziyaretçi';

  return (
    <Paper
      variant="outlined"
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '320px 1fr' },
        height: { md: 'calc(100vh - 290px)' },
        minHeight: 520,
        overflow: 'hidden',
      }}
    >
      {/* Sol: görüşme listesi */}
      <Box sx={{ borderRight: { md: '1px solid' }, borderColor: { md: 'divider' }, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <Box sx={{ p: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Ziyaretçi, e-posta veya konu"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                </InputAdornment>
              ),
            }}
          />
          {/* Durum süzgeci: eşit genişlikte sayaç kutuları; dar alanda alt satıra kayar, yatay kaydırma yok. */}
          <Box
            role="tablist"
            sx={{
              mt: 1.25,
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(56px, 1fr))',
              gap: 0.5,
            }}
          >
            {FILTERS.map(({ value, label, color }) => {
              const active = filter === value;
              const count = counts[value];
              const alert = value === 'WaitingAgent' && count > 0;
              return (
                <ButtonBase
                  key={value}
                  role="tab"
                  aria-selected={active}
                  onClick={() => setFilter(value)}
                  sx={{
                    flexDirection: 'column',
                    py: 0.6,
                    px: 0.5,
                    borderRadius: 1.5,
                    border: '1px solid',
                    borderColor: active ? 'primary.main' : 'divider',
                    bgcolor: active ? 'primary.main' : alert ? 'rgba(245,158,11,0.08)' : 'transparent',
                    color: active ? 'primary.contrastText' : 'text.primary',
                    transition: 'all .15s',
                    '&:hover': { borderColor: 'primary.main' },
                  }}
                >
                  <Typography sx={{ fontSize: 15, fontWeight: 700, lineHeight: 1.2, color: active ? 'inherit' : alert ? '#d97706' : 'inherit' }}>
                    {count}
                  </Typography>
                  <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: 11, fontWeight: 600, opacity: active ? 0.95 : 0.75, whiteSpace: 'nowrap' }}>
                    {color && !active && <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: color }} />}
                    {label}
                  </Box>
                </ButtonBase>
              );
            })}
          </Box>
        </Box>

        <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
          {loading && sessions.length === 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={22} /></Box>
          )}
          {visible.map((s) => {
            const active = s.id === selectedId;
            const waiting = s.status === 'WaitingAgent';
            return (
              <Box
                key={s.id}
                onClick={() => openSession(s.id)}
                sx={{
                  px: 1.5,
                  py: 1.25,
                  display: 'flex',
                  gap: 1.25,
                  alignItems: 'center',
                  cursor: 'pointer',
                  borderLeft: '3px solid',
                  borderLeftColor: active ? 'primary.main' : 'transparent',
                  bgcolor: active ? 'action.selected' : 'transparent',
                  '&:hover': { bgcolor: active ? 'action.selected' : 'action.hover' },
                }}
              >
                <Badge
                  overlap="circular"
                  variant="dot"
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  sx={{ '& .MuiBadge-badge': { bgcolor: STATUS_DOT[s.status] || '#94a3b8', boxShadow: '0 0 0 2px #fff' } }}
                >
                  <Avatar sx={{ width: 34, height: 34, fontSize: 14, bgcolor: 'grey.200', color: 'text.primary' }}>
                    {name(s).charAt(0).toLocaleUpperCase('tr-TR')}
                  </Avatar>
                </Badge>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={1}>
                    <Typography variant="body2" fontWeight={s.unread ? 700 : 600} noWrap>{name(s)}</Typography>
                    <Typography variant="caption" color={waiting ? 'warning.main' : 'text.secondary'} sx={{ flexShrink: 0, fontWeight: waiting ? 600 : 400 }}>
                      {relativeTime(s.lastMessageAt)}
                    </Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {waiting ? `Bekliyor${s.waitStartedAt ? ` · ${waitText(s.waitStartedAt)}` : ''}` : STATUS_META[s.status]?.label || s.status}
                      {s.topic ? ` · ${topicLabel(topics, s.topic)}` : ''}
                    </Typography>
                    {waiting && waitLimit > 0 && s.waitStartedAt && Date.now() - s.waitStartedAt >= waitLimit * 60000 && (
                      <Tooltip title={`${waitLimit} dk içinde yanıt verilmedi; ziyaretçi talebini yenileyebilir.`}>
                        <Box sx={{ flexShrink: 0, px: 0.75, borderRadius: 1, bgcolor: 'error.main', color: '#fff', fontSize: 10, fontWeight: 700, lineHeight: '18px' }}>
                          Süre aşıldı
                        </Box>
                      </Tooltip>
                    )}
                    {!!s.unread && (
                      <Box sx={{ minWidth: 18, height: 18, px: 0.5, borderRadius: 9, bgcolor: 'error.main', color: '#fff', fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {s.unread}
                      </Box>
                    )}
                  </Stack>
                </Box>
              </Box>
            );
          })}
          {!loading && visible.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>
              Bu filtrede görüşme yok.
            </Typography>
          )}
        </Box>
      </Box>

      {/* Sağ: görüşme */}
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, bgcolor: '#fafbfc' }}>
        {!selected ? (
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1, p: 4, color: 'text.secondary' }}>
            <ChatBubbleOutlineIcon sx={{ fontSize: 40, color: 'text.disabled' }} />
            <Typography fontWeight={600}>Bir görüşme seçin</Typography>
            <Typography variant="body2" textAlign="center">
              Bekleyen görüşmeler listenin en üstünde. Yanıt alanında <b>/</b> yazarak hazır yanıt arayabilirsiniz.
            </Typography>
          </Box>
        ) : (
          <>
            {/* Başlık */}
            <Box sx={{ px: 2, py: 1.25, bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.main', fontSize: 15 }}>
                {name(selected).charAt(0).toLocaleUpperCase('tr-TR')}
              </Avatar>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography fontWeight={700} noWrap>{name(selected)}</Typography>
                  <Chip size="small" label={STATUS_META[selected.status]?.label || selected.status} color={STATUS_META[selected.status]?.color || 'default'} sx={{ height: 20, fontSize: 11 }} />
                  {selected.topic && <Chip size="small" variant="outlined" label={topicLabel(topics, selected.topic)} sx={{ height: 20, fontSize: 11 }} />}
                </Stack>
                {selected.visitorEmail ? (
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Typography variant="caption" color="text.secondary" noWrap>{selected.visitorEmail}</Typography>
                    <Tooltip title="Kopyala">
                      <IconButton size="small" sx={{ p: 0.25 }} onClick={() => navigator.clipboard?.writeText(selected.visitorEmail || '')}>
                        <ContentCopyIcon sx={{ fontSize: 13 }} />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                ) : (
                  <Typography variant="caption" color="text.secondary">İletişim bilgisi bırakılmadı</Typography>
                )}
              </Box>
              {selected.status !== 'Active' && !closed && (
                <Button size="small" variant="contained" disableElevation onClick={claim}>Üstlen</Button>
              )}
              {selected.status === 'Active' && selected.claimedByName && (
                <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'none', lg: 'block' } }}>
                  {selected.claimedByName}
                </Typography>
              )}
              {!closed && (
                <Tooltip title="Görüşmeyi kapat">
                  <IconButton size="small" onClick={close}><CloseIcon fontSize="small" /></IconButton>
                </Tooltip>
              )}
            </Box>

            {/* Ziyaretçinin bot akışı */}
            {path.length > 0 && (
              <Box sx={{ px: 2, py: 0.75, bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 0.5, overflowX: 'auto', whiteSpace: 'nowrap' }}>
                <SmartToyOutlinedIcon sx={{ fontSize: 15, color: 'text.disabled', mr: 0.5 }} />
                {path.slice(-5).map((step, i, arr) => (
                  <React.Fragment key={`${step}-${i}`}>
                    <Typography variant="caption" color={i === arr.length - 1 ? 'text.primary' : 'text.secondary'} fontWeight={i === arr.length - 1 ? 600 : 400}>
                      {step.length > 40 ? `${step.slice(0, 40)}…` : step}
                    </Typography>
                    {i < arr.length - 1 && <ChevronRightIcon sx={{ fontSize: 14, color: 'text.disabled' }} />}
                  </React.Fragment>
                ))}
              </Box>
            )}

            {/* Mesajlar */}
            <Box ref={messagesRef} sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 2, py: 2 }}>
              {messages.map((m) => {
                if (m.senderType === 'System') {
                  return (
                    <Typography key={m.id} variant="caption" component="p" color="text.secondary" sx={{ textAlign: 'center', my: 1.5 }}>
                      {m.body} · {clockTime(m.createdAt)}
                    </Typography>
                  );
                }
                const mine = m.senderType === 'Agent';
                const bot = m.senderType === 'Bot';
                return (
                  <Box key={m.id} sx={{ display: 'flex', flexDirection: 'column', alignItems: mine ? 'flex-end' : 'flex-start', mb: 1.25 }}>
                    <Box
                      sx={{
                        maxWidth: '72%',
                        px: 1.5,
                        py: 1,
                        borderRadius: 2.5,
                        borderBottomRightRadius: mine ? 6 : undefined,
                        borderBottomLeftRadius: mine ? undefined : 6,
                        bgcolor: mine ? 'primary.main' : bot ? '#eef2f7' : 'background.paper',
                        color: mine ? 'primary.contrastText' : 'text.primary',
                        border: mine || bot ? 'none' : '1px solid',
                        borderColor: 'divider',
                        fontSize: 14,
                        lineHeight: 1.5,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                      }}
                    >
                      {m.body}
                    </Box>
                    <Typography variant="caption" color="text.disabled" sx={{ mt: 0.25, px: 0.5, fontSize: 10.5 }}>
                      {bot ? 'Bot · ' : mine && m.senderName ? `${m.senderName} · ` : ''}{clockTime(m.createdAt)}
                    </Typography>
                  </Box>
                );
              })}
            </Box>

            {/* Yanıt alanı */}
            <Box sx={{ bgcolor: 'background.paper', borderTop: '1px solid', borderColor: 'divider', p: 1.5, position: 'relative' }}>
              {closed ? (
                <Typography variant="body2" color="text.secondary" textAlign="center">Görüşme kapatıldı.</Typography>
              ) : (
                <>
                  {slashMatches.length > 0 && (
                    <Paper elevation={6} sx={{ position: 'absolute', left: 12, right: 12, bottom: '100%', mb: 0.5, overflow: 'hidden', zIndex: 2 }}>
                      {slashMatches.map((t, i) => (
                        <Box
                          key={t.id || t.title}
                          onMouseDown={(e) => { e.preventDefault(); insertTemplate(t); }}
                          onMouseEnter={() => setSlashIndex(i)}
                          sx={{ px: 1.5, py: 1, cursor: 'pointer', bgcolor: i === slashIndex ? 'action.selected' : 'transparent' }}
                        >
                          <Typography variant="body2" fontWeight={600}>{t.title}</Typography>
                          <Typography variant="caption" color="text.secondary" noWrap component="p">{t.body}</Typography>
                        </Box>
                      ))}
                    </Paper>
                  )}
                  {templates.length > 0 && (
                    <Stack direction="row" spacing={0.75} sx={{ mb: 1, overflowX: 'auto', pb: 0.25 }}>
                      {templates.map((t) => (
                        <Chip key={t.id || t.title} size="small" variant="outlined" label={t.title} onClick={() => insertTemplate(t)} sx={{ flexShrink: 0 }} />
                      ))}
                    </Stack>
                  )}
                  <Stack direction="row" spacing={1} alignItems="flex-end">
                    <TextField
                      inputRef={inputRef}
                      fullWidth
                      multiline
                      maxRows={5}
                      size="small"
                      placeholder={selected.status === 'Active' ? 'Yanıt yazın  ·  / hazır yanıt  ·  Shift+Enter yeni satır' : 'Yazdığınızda görüşmeyi üstlenirsiniz'}
                      value={draft}
                      onChange={(e) => setDraft(e.target.value)}
                      onKeyDown={onKeyDown}
                    />
                    <IconButton color="primary" onClick={send} disabled={!draft.trim() || sending || draft.startsWith('/')} sx={{ bgcolor: 'primary.main', color: '#fff', '&:hover': { bgcolor: 'primary.dark' }, '&.Mui-disabled': { bgcolor: 'action.disabledBackground' } }}>
                      {sending ? <CircularProgress size={18} color="inherit" /> : <SendIcon fontSize="small" />}
                    </IconButton>
                  </Stack>
                  <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
                    Görünen ad: {agentName}
                  </Typography>
                </>
              )}
            </Box>
          </>
        )}
      </Box>
    </Paper>
  );
};

export default LiveChatInbox;
